import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useNavigate } from "react-router-dom";
import { User } from "../../types";
import { getWeb29OfferState, WEB29_OFFER_CHECKOUT_GRACE_MS } from "../../web29Offer";
import {
  addDays,
  annualPriceLabel,
  formatShortDate,
  fromInputDate,
  isValidCustomDate,
  offsetDays,
  RESERVATION_MAX_DAYS,
  RESERVATION_PRESETS,
  ReservationPreset,
  startOfDay,
  toInputDate,
} from "./reservation";
import { RESERVATION_EVENTS, trackReservation } from "./tracking";

// web41 reservation pitch page (/reserve). Fake door: picking a date and
// clicking Reserve only logs events and leads to the coming-soon screen.
export const Page = styled.div`
  min-height: 100vh;
  background: #f5f6fa;
  font-family: Lato, sans-serif;
  color: #1a2433;
`;

export const TopBar = styled.div`
  background: #ffffff;
  padding: 18px 16px;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #2b3445;
  box-shadow: 0 1px 0 #e6e9f0;
`;

export const Container = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 28px 20px 170px;
`;

export const Headline = styled.h1`
  margin: 0;
  font-size: 30px;
  line-height: 36px;
  font-weight: 900;
  text-align: center;
  span { color: #4f6bff; }
`;

export const Sub = styled.p`
  margin: 12px 0 0;
  text-align: center;
  font-size: 18px;
  line-height: 26px;
  color: #6b7688;
`;

const Question = styled.h2`
  margin: 32px 0 12px;
  font-size: 18px;
  font-weight: 800;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const tileCss = css<{ $selected: boolean }>`
  position: relative;
  box-sizing: border-box;
  display: block;
  width: 100%;
  margin: 0;
  padding: 18px 8px;
  background: ${({ $selected }) => ($selected ? "#eef1ff" : "#ffffff")};
  border: 2px solid ${({ $selected }) => ($selected ? "#4f6bff" : "#e1e6f0")};
  border-radius: 14px;
  font-family: Lato, sans-serif;
  color: #1a2433;
  cursor: pointer;
  text-align: center;

  strong { display: block; font-size: 19px; font-weight: 800; line-height: 24px; }
  small { display: block; margin-top: 4px; font-size: 15px; color: #6b7688; }
`;

const Tile = styled.button<{ $selected: boolean }>`
  ${tileCss}
`;

// The custom tile is a <label> with the real date input stretched over it, so
// a tap lands on the native control: iOS/Android open their date wheel, and
// desktop browsers open their calendar (showPicker on the click).
const CustomTile = styled.label<{ $selected: boolean }>`
  ${tileCss}
`;

const DateInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  font-size: 16px; /* iOS: never zoom on focus */
`;

const InfoBox = styled.ul`
  list-style: none;
  margin: 20px 0 0;
  padding: 16px;
  background: #eef2ff;
  border: 1px solid #c9d4ff;
  border-radius: 14px;

  li { display: flex; gap: 10px; font-size: 16px; line-height: 24px; }
  li + li { margin-top: 8px; }
  li span:first-child { flex: 0 0 22px; text-align: center; }
`;

export const BottomBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 16px calc(20px + env(safe-area-inset-bottom, 0px));
  background: #f5f6fa;
  box-shadow: 0 -8px 16px rgba(245, 246, 250, 0.9);
`;

export const BottomInner = styled.div`
  max-width: 560px;
  margin: 0 auto;
  text-align: center;
`;

export const Cta = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 0;
  background: #dfb140;
  border: 0;
  border-radius: 8px;
  font-family: Oswald, sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 37px;
  text-transform: uppercase;
  color: #000000;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
`;

export const CtaCaption = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: #7d8da6;
`;

type Selection = { preset: ReservationPreset; date: Date };

export function ReservationPage({ user }: { user: User }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!user || !user.focus) navigate("/");
  }, [user, navigate]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const [selection, setSelection] = useState<Selection | null>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // "Today's price": whatever the pricing page the visitor came from shows.
  const priceLabel = useMemo(
    () => annualPriceLabel(getWeb29OfferState(Date.now(), WEB29_OFFER_CHECKOUT_GRACE_MS).isDiscountActive),
    []
  );

  const choose = (preset: ReservationPreset, date: Date) => {
    setSelection({ preset, date });
    trackReservation(RESERVATION_EVENTS.dateSelected, { preset, offset_days: offsetDays(today, date) });
  };

  const onCustomDate = (value: string) => {
    const date = fromInputDate(value);
    if (date && isValidCustomDate(date, today)) choose("custom", date);
  };

  const onReserve = () => {
    if (!selection) return;
    const offset = offsetDays(today, selection.date);
    trackReservation(RESERVATION_EVENTS.reserveClicked, {
      preset: selection.preset,
      offset_days: offset,
      start_date: toInputDate(selection.date),
    });
    navigate("/reserve/soon", {
      state: { preset: selection.preset, offsetDays: offset, startDate: toInputDate(selection.date) },
    });
  };

  const customSelected = selection?.preset === "custom";

  return (
    <Page>
      <TopBar>Reserve your start date</TopBar>
      <Container>
        <Headline>
          Turn &quot;someday&quot; into&nbsp;<span>a&nbsp;date</span>.
        </Headline>
        <Sub>&quot;Someday&quot; almost never comes. A date on the calendar does.</Sub>

        <Question>When will you start?</Question>
        <Tiles>
          {RESERVATION_PRESETS.map((preset) => {
            const date = addDays(today, preset.days);
            return (
              <Tile
                key={preset.id}
                type="button"
                $selected={selection?.preset === preset.id}
                onClick={() => choose(preset.id, date)}
              >
                <strong>{preset.label}</strong>
                <small>{formatShortDate(date)}</small>
              </Tile>
            );
          })}
          <CustomTile $selected={!!customSelected} data-reservation-custom-tile>
            <strong>Pick a date</strong>
            <small>{customSelected && selection ? formatShortDate(selection.date) : `Up to ${RESERVATION_MAX_DAYS} days out`}</small>
            <DateInput
              ref={dateInputRef}
              type="date"
              aria-label="Pick a start date"
              min={toInputDate(addDays(today, 1))}
              max={toInputDate(addDays(today, RESERVATION_MAX_DAYS))}
              value={customSelected && selection ? toInputDate(selection.date) : ""}
              onChange={(e) => onCustomDate(e.target.value)}
              onClick={(e) => {
                // Desktop browsers focus a date segment on click; ask for the
                // calendar explicitly (allowed inside a user gesture).
                const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
                try { el.showPicker?.(); } catch { /* unsupported or already open */ }
              }}
            />
          </CustomTile>
        </Tiles>

        <InfoBox>
          <li>
            <span>🔒</span>
            <span>
              <strong>Today&apos;s price, guaranteed.</strong> {priceLabel} locked for your reservation, even if prices go up.
            </span>
          </li>
          <li>
            <span>💳</span>
            <span>
              <strong>Nothing charged</strong> until your trial ends.
            </span>
          </li>
          <li>
            <span>⚡</span>
            <span>
              Feeling ready earlier? <strong>Start sooner anytime.</strong>
            </span>
          </li>
        </InfoBox>
      </Container>

      <BottomBar>
        <BottomInner>
          <Cta type="button" $disabled={!selection} disabled={!selection} onClick={onReserve} data-reservation-cta="reserve">
            Reserve my start date
          </Cta>
          <CtaCaption>
            {selection ? `Nothing charged today. Your trial would start ${formatShortDate(selection.date)}.` : "Nothing charged today. Pick a start date above."}
          </CtaCaption>
        </BottomInner>
      </BottomBar>
    </Page>
  );
}
